import type { Feed, FeedItem, FeedMetadata } from '@knocklabs/client'

// The cross-tab transport for the inbox. One BroadcastChannel per feed + user
// carries every message Orbit publishes about that feed, and one listener per
// browser tab consumes all of them, so the channel is opened in exactly two
// places: here, and the publisher helpers below.
//
// Orbit publishes its own events rather than consuming the ones the Knock SDK
// broadcasts. The SDK emits `items:archived` even when the server rejected the
// mutation with a 4xx (its message client returns the error instead of
// throwing), so acting on it would let one tab's failed archive delete a live
// row in every other tab. Orbit only publishes what the server confirmed.

const ARCHIVED_EVENT = 'seen:inbox:archived'
const METADATA_EVENT = 'seen:inbox:metadata'
const PAGE_EVENT = 'seen:inbox:page'

interface ArchivedMessage {
  type: typeof ARCHIVED_EVENT
  itemIds: string[]
}

interface MetadataMessage {
  type: typeof METADATA_EVENT
  metadata: FeedMetadata
  // Wall-clock of the request that produced this snapshot. Receivers drop
  // anything older than what they have already applied, so a slow response
  // overtaken by a faster later one cannot resurrect a stale count.
  fetchedAt: number
}

// The first page of the feed as one tab read it, so the others can converge
// their read state and reappearances without a request of their own.
interface PageMessage {
  type: typeof PAGE_EVENT
  entries: FeedItem[]
  fetchedAt: number
}

function isPageMessage(data: unknown): data is PageMessage {
  if (typeof data !== 'object' || data === null) {
    return false
  }
  const candidate = data as Partial<PageMessage>
  return (
    candidate.type === PAGE_EVENT &&
    typeof candidate.fetchedAt === 'number' &&
    Array.isArray(candidate.entries) &&
    candidate.entries.every(
      (entry) =>
        typeof entry === 'object' &&
        entry !== null &&
        typeof (entry as FeedItem).id === 'string'
    )
  )
}

function isArchivedMessage(data: unknown): data is ArchivedMessage {
  if (typeof data !== 'object' || data === null) {
    return false
  }
  const candidate = data as Partial<ArchivedMessage>
  return (
    candidate.type === ARCHIVED_EVENT &&
    Array.isArray(candidate.itemIds) &&
    candidate.itemIds.every((id) => typeof id === 'string')
  )
}

function isMetadataMessage(data: unknown): data is MetadataMessage {
  if (typeof data !== 'object' || data === null) {
    return false
  }
  const candidate = data as Partial<MetadataMessage>
  return (
    candidate.type === METADATA_EVENT &&
    typeof candidate.fetchedAt === 'number' &&
    typeof candidate.metadata === 'object' &&
    candidate.metadata !== null &&
    typeof candidate.metadata.total_count === 'number' &&
    typeof candidate.metadata.unread_count === 'number' &&
    typeof candidate.metadata.unseen_count === 'number'
  )
}

// Same name in every tab for a given feed + user, so impersonation (which
// remounts the feed client for a different Knock user) is naturally scoped to
// its own channel. Note the inbox and Done feeds share this name, because a
// Feed's id does not encode its query — which is why exactly one coordinator,
// bound explicitly to the inbox feed, may listen.
export function inboxChannelName(feedClient: Feed): string | undefined {
  const userId = feedClient.knock.userId
  return userId ? `seen:inbox:${feedClient.feedId}:${userId}` : undefined
}

export function openInboxChannel(
  feedClient: Feed
): BroadcastChannel | undefined {
  const name = inboxChannelName(feedClient)
  if (!name || typeof BroadcastChannel === 'undefined') {
    return undefined
  }
  return new BroadcastChannel(name)
}

function postToInboxChannel(
  feedClient: Feed,
  message: ArchivedMessage | MetadataMessage | PageMessage
): void {
  const channel = openInboxChannel(feedClient)
  if (!channel) {
    return
  }
  channel.postMessage(message)
  channel.close()
}

export function broadcastArchivedInboxItems(
  feedClient: Feed,
  itemIds: string[]
): void {
  if (itemIds.length === 0) {
    return
  }
  postToInboxChannel(feedClient, { type: ARCHIVED_EVENT, itemIds })
}

export function broadcastInboxMetadata(
  feedClient: Feed,
  metadata: FeedMetadata,
  fetchedAt: number
): void {
  postToInboxChannel(feedClient, { type: METADATA_EVENT, metadata, fetchedAt })
}

export function broadcastInboxPage(
  feedClient: Feed,
  entries: FeedItem[],
  fetchedAt: number
): void {
  postToInboxChannel(feedClient, { type: PAGE_EVENT, entries, fetchedAt })
}

export function subscribeToInboxSync(
  feedClient: Feed,
  handlers: {
    onArchived: (itemIds: string[]) => void
    onMetadata: (metadata: FeedMetadata, fetchedAt: number) => void
    onPage: (entries: FeedItem[], fetchedAt: number) => void
  }
): () => void {
  const channel = openInboxChannel(feedClient)
  if (!channel) {
    return () => {}
  }
  channel.onmessage = (event: MessageEvent) => {
    if (isArchivedMessage(event.data)) {
      handlers.onArchived(event.data.itemIds)
      return
    }
    if (isMetadataMessage(event.data)) {
      handlers.onMetadata(event.data.metadata, event.data.fetchedAt)
      return
    }
    if (isPageMessage(event.data)) {
      handlers.onPage(event.data.entries, event.data.fetchedAt)
    }
  }
  return () => {
    channel.close()
  }
}

// Collapses work that several tabs would otherwise each do at the same moment.
// `ifAvailable` makes this a coalescer rather than a leadership election: the
// tab that gets the lock does the work and broadcasts the result, and the tabs
// that were racing it skip theirs and apply the broadcast instead. Nothing is
// held across a tab's lifetime, so there is no leader to hand over and a
// crashed tab starves nobody. Where Web Locks is unavailable every tab does its
// own work, which is what happens today.
export async function withInboxSyncLock(
  feedClient: Feed,
  run: () => Promise<void>,
  // The sweep holds its lock across a ten-page walk, task lookups and archives.
  // Metadata takes a lock of its own, so a badge refresh coalescing behind a
  // sweep is not dropped by `ifAvailable` and left to the 90s poll. The sweep
  // keeps the original scope name so tabs on either side of a deploy still
  // coordinate their sweeps.
  scope: 'sync' | 'metadata' = 'sync'
): Promise<void> {
  const name = inboxChannelName(feedClient)
  if (!name || typeof navigator === 'undefined' || !navigator.locks) {
    await run()
    return
  }
  await navigator.locks.request(
    `${name}:${scope}`,
    { mode: 'exclusive', ifAvailable: true },
    async (lock) => {
      if (!lock) {
        return
      }
      await run()
    }
  )
}
